import { GetServerSideProps, InferGetServerSidePropsType } from 'next'

import { useStatsigNewCLPFilterExperiment } from '@src/pages/[...slug]/components/collection/hooks/useStatsigNewCLPFilterExperiment'
import { ECollectionPageType } from '@src/pages/[...slug]/components/collection/services/collectionService/CollectionService.types'
// @i18n-ignore-file
import { PAGE_PATH } from '@src/quince-site-common/src/constants/pageData'
import { getSeoMetapageData } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { fetchRegionDetails } from '@src/server-side/api/fetch-region-details'
import {
  CollectionServerSideFeatureFlags,
  FEATURE_FLAG_TYPE,
  FILTER_TYPE,
  URL_PLACEMENT,
} from '@src/server-side/feature-flags/feature-flag.types'
import { getServerSideBaseProps } from '@src/server-side/get-server-side-props'
import { negotiateLocale } from '@src/server-side/utils/server-props.utils'

import { CollectionFeatureProvider } from '../[...slug]/components/collection/collection-feature-provider'
import Layout from '../layout'

import SearchPage from './search-page'
import {
  SearchResultsSectionConfiguratorContextType,
  SearchResultsSectionConfiguratorProvider,
} from './searchResultsSection/SearchResultsSectionConfigurator'

export const DEFAULT_FEATURE_FLAGS_FOR_SEARCH: CollectionServerSideFeatureFlags = {
  featureFlagType: FEATURE_FLAG_TYPE.COLLECTION,
  subCollectionUrlPlacement: URL_PLACEMENT.PATH,
  collectionPageType: ECollectionPageType.GROUPED,
  filterType: FILTER_TYPE.DROPDOWN,
  isDynamicCollection: false,
  bestSellerExperimentBucket: null,
  clpFilterExperimentBucketMobile: null,
  clpFilterExperimentBucketDesktop: null,
  clpPersonalisationExperimentBucket: null,
  velouFiltersExperimentBucket: null,
  isBestSellerPersonalizedExp: false,
  isPersonalized: false,
}

const SLUG = 'search'
const SEO_TITLE = 'Search'
const SEO_DESCRIPTION = 'Search items from Quince'

export const getServerSideProps = (async ctx => {
  const serverSideBaseProps = await getServerSideBaseProps(ctx, PAGE_PATH.SEARCH)

  // Safe access to isI18nPreviewEnabled with fallback
  const isI18nPreviewEnabled =
    'props' in serverSideBaseProps ? (await serverSideBaseProps.props).isI18nPreviewEnabled : false

  const regionDetails = await fetchRegionDetails({ isI18nPreviewEnabled })
  const { locale } = negotiateLocale(ctx, regionDetails, isI18nPreviewEnabled)
  if ('props' in serverSideBaseProps) {
    const props = await serverSideBaseProps.props
    /* eslint-disable i18next/no-literal-string */
    return {
      props: {
        ...props,
        seoMetaData: getSeoMetapageData(
          props.isI18nPreviewEnabled,
          {
            slug: SLUG,
            seoTitle: SEO_TITLE,
            seoDescription: SEO_DESCRIPTION,
          },
          locale,
          regionDetails,
        ),
      },
    }
    /* eslint-enable i18next/no-literal-string */
  }
  return serverSideBaseProps
}) satisfies GetServerSideProps<ServerSideBaseProps>

export default function PageSearch(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <Layout {...props}>
      <CollectionFeatureProvider serverSideFeatureFlags={DEFAULT_FEATURE_FLAGS_FOR_SEARCH}>
        <SearchResultsSectionConfiguratorWrapper>
          <SearchPage />
        </SearchResultsSectionConfiguratorWrapper>
      </CollectionFeatureProvider>
    </Layout>
  )
}

function SearchResultsSectionConfiguratorWrapper({ children }: { children: React.ReactNode }) {
  const {
    isNewCLPFiltersExperimentActive,
    showDesktopFilterWithSidebar,
    showMobileFilterWithDrawer,
    isNewCLPFiltersExperimentInitialized,
    showDesktopFilterWithDrawer,
  } = useStatsigNewCLPFilterExperiment()

  const searchPageConfig: SearchResultsSectionConfiguratorContextType = {
    isNewCLPFiltersExperimentActive,
    showDesktopFilterWithSidebar,
    showMobileFilterWithDrawer,
    isNewCLPFiltersExperimentInitialized,
    showDesktopFilterWithDrawer,
  }

  return (
    <SearchResultsSectionConfiguratorProvider config={searchPageConfig}>
      {children}
    </SearchResultsSectionConfiguratorProvider>
  )
}
